import { ElementRef, inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { PasConfirmationService } from '@porsche-aftersales/frontend-toolbox';
import { filter, first, map, merge, Observable, of, switchMap, tap } from 'rxjs';

import {
  CreateSupplierConditionRestRequest,
  RetrieveSupplierConditionsRestResponse,
  UpdateSupplierConditionRestRequest,
} from '../../../api/tur-wr/models';
import { SupplierConditionService } from '../../../api/tur-wr/services';
import { NotificationServiceWrapper } from '../../../services/notification-service-wrapper/notification-service-wrapper';
import { UserRights } from '../../../services/user-service/user-rights.enum';
import { UserService } from '../../../services/user-service/user.service';
import { translate } from '../../../shared/fn/translate';
import { GlobalStore } from '../../../shared/global.store';

import { EnumType } from './supplier-condition.component';

export const isDateExpired = (validToDateAsString: string): boolean => {
  const todaysDate = new Date();
  const validToDate = new Date(validToDateAsString);
  return validToDate < todaysDate;
};

export const ASTERISK = '*';

export const getKeyByValueIgnoreCase = <T>(enumeration: EnumType<T>, value: string): string | undefined =>
  Object.keys(enumeration).find(key => enumeration[key]?.toUpperCase() === value?.toUpperCase());

export type SupplierCondition = RetrieveSupplierConditionsRestResponse;

function decimalPlacesMax(max = 2): ValidatorFn {
  return (c: AbstractControl): ValidationErrors | null => {
    const v = c.value;
    if (v === null || v === undefined || v === '') return { required: true };
    if (typeof v !== 'number' || !Number.isFinite(v)) return { pattern: true };
    if (v < 0) return { min: true };
    const dec = (Math.abs(v).toString().split('.')[1] || '').length;
    return dec <= max ? null : { pattern: true };
  };
}

interface SupplierConditionFormData {
  id: string;
  thirdPartyCostsFactor: number;
  thirdPartyMaterialCostsFactor: number;
  handlingChargeCostsFactor: number;
  handlingSupplierCostsFactor: number;
  laborCostsFactor: number;
  landingCostsFactor: number;
  materialCostsFactor: number;
  otherCosts: number;
  supplierNumber?: string;
  supplierName?: string;
  createDate: string;
  lastEditDate: string;
}

const defaultState = { supplierCondition: [], formGroup: undefined, modalTemplate: undefined, loading: false };
const ERROR_KEY_DUPLICATE_SUPPLIER = 'WR_RC_SUPPLIER_CONDITION_ALREADY_EXIST';
const CONST_REG_SUPPLIER_NUMBER = /^\d{6}$/;
const CONST_REG_AMOUNT = /^\d{0,4}$/;
const GERMAN_DECIMAL_PATTERN = /^(?!-)\d+(,\d{1,2})?$/;
const ENGLISH_DECIMAL_PATTERN = /^(?!-)\d+(\.\d{1,2})?$/;

export function getLocalizedEuroPattern(translateService: TranslateService): RegExp {
  return translateService.currentLang === 'de' ? GERMAN_DECIMAL_PATTERN : ENGLISH_DECIMAL_PATTERN;
}

interface SupplierConditionState {
  supplierCondition: SupplierCondition[];
  formGroup: FormGroup;
  modalTemplate: ElementRef;
  loading: boolean;
}

@Injectable()
export class SupplierConditionStore extends ComponentStore<SupplierConditionState> {
  public readonly supplierCondition$: Observable<SupplierCondition[]> = this.select(state => state.supplierCondition);
  public readonly loading$: Observable<boolean> = this.select(state => state.loading);
  public readonly formGroup$ = this.select(state => state.formGroup);
  public readonly isModalOpenInDisplayOnlyMode$: Observable<boolean> = of(false);

  public readonly isFormValid$ = this.formGroup$.pipe(
    filter(Boolean),
    switchMap((form): Observable<FormControlStatus> =>
      merge(
        of(form.valid).pipe(map((isValid: boolean): FormControlStatus => (isValid ? 'VALID' : 'INVALID'))),
        form.statusChanges,
      ),
    ),
    map((status: FormControlStatus) => status === 'VALID'),
  );

  public readonly isFormValidAndModalNotOpenInDisplayOnlyMode$ = this.select(
    this.isFormValid$,
    this.isModalOpenInDisplayOnlyMode$,
    (isFormValid, isModalOpenInDisplayOnlyMode) => isFormValid && !isModalOpenInDisplayOnlyMode,
  );

  private readonly supplierConditionService = inject(SupplierConditionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly globalStore = inject(GlobalStore);
  private readonly notificationServiceWrapper = inject(NotificationServiceWrapper);
  private readonly pasConfirmationService = inject(PasConfirmationService);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(UserService);

  public constructor() {
    super(defaultState);
  }

  public ngrxOnStoreInit(): void {
    this.loadSupplierConditions();
  }

  public readonly loadSupplierConditions = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.patchState({ loading: true })),
      switchMap(() => this.supplierConditionService.findAllSupplierConditions()),
      tap((supplierCondition: SupplierCondition[]) => {
        supplierCondition.forEach(item => {
          item.materialCostsFactor = Math.round(item.materialCostsFactor * 100);
          item.laborCostsFactor = Math.round(item.laborCostsFactor * 100);
          item.thirdPartyCostsFactor = Math.round(item.thirdPartyCostsFactor * 100);
          item.thirdPartyMaterialCostsFactor = Math.round(item.thirdPartyMaterialCostsFactor * 100);
          item.handlingChargeCostsFactor = Math.round(item.handlingChargeCostsFactor * 100);
          item.handlingSupplierCostsFactor = Math.round(item.handlingSupplierCostsFactor * 100);
          item.landingCostsFactor = Math.round(item.landingCostsFactor * 100);
        });
        this.patchState({ supplierCondition, loading: false });
      }),
    ),
  );

  public readonly addSupplierCondition = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.initForm()),
      switchMap(() => this.select(state => state.modalTemplate).pipe(first())),
      tap((elementRef: ElementRef) => this.globalStore.openModal(elementRef)),
    ),
  );

  public readonly editSupplierCondition = this.effect((id$: Observable<string>) =>
    id$.pipe(
      tap(() => this.globalStore.setProcessing(true)),
      switchMap(id => this.supplierConditionService.findSupplierConditionById({ id: id })),
      tap((supplierCondition: SupplierCondition) => this.initForm(supplierCondition)),
      switchMap(() => this.select(state => state.modalTemplate).pipe(first())),
      tap((elementRef: ElementRef) => {
        this.globalStore.openModal(elementRef);
        this.globalStore.setProcessing(false);
      }),
    ),
  );

  public readonly deleteSupplierCondition = this.effect((id$: Observable<string>) =>
    id$.pipe(
      tap(async (id: string) => {
        const dialogTitle = translate('supplierCondition.delete.dialog.title');
        const dialogMessage = translate('supplierCondition.delete.dialog.message');
        const dismissLabel = translate('CANCEL');
        return this.pasConfirmationService
          .showConfirmation(
            dialogTitle,
            dialogMessage,
            this.displayDeleteSupplierConditionButton(),
            dismissLabel,
            false,
          )
          .then(
            () => {
              // confirmed
              this.globalStore.setProcessing(true);

              this.supplierConditionService.deleteSupplierConditionById({ id }).subscribe({
                next: () => {
                  this.globalStore.setProcessing(false);

                  const title = translate('supplierCondition.saveSuccess.title');
                  const content = translate('supplierCondition.delete.success.message');
                  this.notificationServiceWrapper.addSuccessNotification(title, content);

                  // reload table
                  this.loadSupplierConditions();
                },
                error: () => {
                  this.globalStore.setProcessing(false);

                  const title = translate('supplierCondition.saveError.title');
                  const content = translate('supplierCondition.delete.error.message');
                  this.notificationServiceWrapper.addErrorNotification(title, content);
                },
              });
            },
            () => {
              // dismissed - do nothing
            },
          );
      }),
    ),
  );

  private displayDeleteSupplierConditionButton(): string {
    return this.userService.hasRight(UserRights.RECOURSE_BOOKING) ? translate('OK') : null;
  }

  public readonly saveOrUpdateSupplierCondition = this.effect(trigger$ => {
    const createSavePayload = (data: SupplierConditionFormData): CreateSupplierConditionRestRequest => ({
      supplierNumber: data.supplierNumber,
      thirdPartyCostsFactor: data.thirdPartyCostsFactor / 100,
      thirdPartyMaterialCostsFactor: data.thirdPartyMaterialCostsFactor / 100,
      handlingChargeCostsFactor: data.handlingChargeCostsFactor / 100,
      handlingSupplierCostsFactor: data.handlingSupplierCostsFactor / 100,
      laborCostsFactor: data.laborCostsFactor / 100,
      landingCostsFactor: data.landingCostsFactor / 100,
      materialCostsFactor: data.materialCostsFactor / 100,
      otherCosts: data.otherCosts,
    });

    const createUpdatePayload = (data: SupplierConditionFormData): UpdateSupplierConditionRestRequest => ({
      thirdPartyCostsFactor: data.thirdPartyCostsFactor / 100,
      thirdPartyMaterialCostsFactor: data.thirdPartyMaterialCostsFactor / 100,
      handlingChargeCostsFactor: data.handlingChargeCostsFactor / 100,
      handlingSupplierCostsFactor: data.handlingSupplierCostsFactor / 100,
      laborCostsFactor: data.laborCostsFactor / 100,
      landingCostsFactor: data.landingCostsFactor / 100,
      materialCostsFactor: data.materialCostsFactor / 100,
      otherCosts: data.otherCosts,
    });

    return trigger$.pipe(
      tap(() => this.globalStore.setProcessing(true)),
      switchMap(() => this.formGroup$.pipe(first())),
      map(formGroup => formGroup.getRawValue()),
      map((data: SupplierConditionFormData) => {
        if (typeof data.otherCosts === 'string') {
          data.otherCosts = this.parseLocalizedNumber(data.otherCosts);
        }
        return data;
      }),
      switchMap((data: SupplierConditionFormData) => {
        const isAboutInitialCreate = data.id == null;
        const serviceCall = isAboutInitialCreate
          ? this.supplierConditionService.createSupplierCondition({ body: createSavePayload(data) })
          : this.supplierConditionService.updateSupplierCondition({
              id: data.id,
              body: createUpdatePayload(data),
            });
        return serviceCall.pipe(
          tapResponse({
            next: () => {
              const title = translate('supplierCondition.saveSuccess.title');
              const content = translate('supplierCondition.saveSuccess.content');
              this.notificationServiceWrapper.addSuccessNotification(title, content);
            },
            error: (error: any) => {
              const title = translate('supplierCondition.saveError.title');
              const getSubKey = (errorKey: string | undefined): string =>
                errorKey === ERROR_KEY_DUPLICATE_SUPPLIER ? 'duplicateSupplier' : 'content';
              const errorKey = error?.error?.exceptionKey;
              const content = translate(`supplierCondition.saveError.${getSubKey(errorKey)}`);
              this.notificationServiceWrapper.addErrorNotification(title, content);
            },
            finalize: () => {
              this.globalStore.closeModal();
              this.globalStore.setProcessing(false);
            },
          }),
          tap(() => this.loadSupplierConditions()),
        );
      }),
    );
  });

  private parseLocalizedNumber(value: string | number): number {
    if (typeof value !== 'string') return value as number;

    if (this.translateService.currentLang === 'de' && value.includes(',')) {
      return Number.parseFloat(value.replace(',', '.'));
    }

    return Number.parseFloat(value);
  }

  public readonly cancelSupplierConditionEditing = this.effect(trigger$ =>
    trigger$.pipe(
      tap(() => this.globalStore.closeModal()),
      tap(() => this.destroyForm),
    ),
  );

  private readonly initForm = this.effect((triggerOrData$: Observable<void | SupplierCondition>) => {
    // eslint-disable-next-line complexity
    const toFormData = (triggerOrData: SupplierCondition | undefined): SupplierConditionFormData =>
      ({
        id: triggerOrData?.id,
        supplierNumber: triggerOrData?.supplierNumber || '',
        supplierName: triggerOrData?.supplierName || '',
        thirdPartyCostsFactor: Math.round(triggerOrData?.thirdPartyCostsFactor * 100) ?? '',
        thirdPartyMaterialCostsFactor: Math.round(triggerOrData?.thirdPartyMaterialCostsFactor * 100) ?? '',
        handlingChargeCostsFactor: Math.round(triggerOrData?.handlingChargeCostsFactor * 100) ?? '',
        handlingSupplierCostsFactor: Math.round(triggerOrData?.handlingSupplierCostsFactor * 100) ?? '',
        laborCostsFactor: Math.round(triggerOrData?.laborCostsFactor * 100) ?? '',
        landingCostsFactor: Math.round(triggerOrData?.landingCostsFactor * 100) ?? '',
        materialCostsFactor: Math.round(triggerOrData?.materialCostsFactor * 100) ?? '',
        otherCosts: triggerOrData?.otherCosts ?? 0,
        createDate: triggerOrData?.createDate,
        lastEditDate: triggerOrData?.lastEditDate,
      }) as SupplierConditionFormData;

    const createFormGroup = (data: SupplierConditionFormData): FormGroup => {
      const isUpdateForm = data.id != null;

      const group = this.formBuilder.group({
        id: this.formBuilder.control({ value: data.id, disabled: true }),
        supplierNumber: this.formBuilder.control({ value: data.supplierNumber, disabled: isUpdateForm }, [
          Validators.required,
          Validators.pattern(CONST_REG_SUPPLIER_NUMBER),
        ]),
        supplierName: this.formBuilder.control({ value: data.supplierName, disabled: true }),
        thirdPartyCostsFactor: this.formBuilder.control({ value: data.thirdPartyCostsFactor, disabled: false }, [
          Validators.required,
          Validators.pattern(CONST_REG_AMOUNT),
        ]),
        thirdPartyMaterialCostsFactor: this.formBuilder.control(
          { value: data.thirdPartyMaterialCostsFactor, disabled: false },
          [Validators.required, Validators.pattern(CONST_REG_AMOUNT)],
        ),
        handlingChargeCostsFactor: this.formBuilder.control(
          { value: data.handlingChargeCostsFactor, disabled: false },
          [Validators.required, Validators.pattern(CONST_REG_AMOUNT)],
        ),
        handlingSupplierCostsFactor: this.formBuilder.control(
          { value: data.handlingSupplierCostsFactor, disabled: false },
          [Validators.required, Validators.pattern(CONST_REG_AMOUNT)],
        ),
        laborCostsFactor: this.formBuilder.control({ value: data.laborCostsFactor, disabled: false }, [
          Validators.required,
          Validators.pattern(CONST_REG_AMOUNT),
        ]),
        landingCostsFactor: this.formBuilder.control({ value: data.landingCostsFactor, disabled: false }, [
          Validators.required,
          Validators.pattern(CONST_REG_AMOUNT),
        ]),
        materialCostsFactor: this.formBuilder.control({ value: data.materialCostsFactor, disabled: false }, [
          Validators.required,
          Validators.pattern(CONST_REG_AMOUNT),
        ]),

        otherCosts: this.formBuilder.control<number>({ value: data.otherCosts ?? 0, disabled: false }, [
          Validators.required,
          decimalPlacesMax(2),
        ]),

        createDate: this.formBuilder.control({ value: data.createDate, disabled: true }),
        lastEditDate: this.formBuilder.control({ value: data.lastEditDate, disabled: true }),
      });

      return group;
    };

    return triggerOrData$.pipe(
      map((triggerOrData: SupplierCondition | undefined): SupplierConditionFormData => toFormData(triggerOrData)),
      tap((data: SupplierConditionFormData) => {
        this.patchState({ formGroup: createFormGroup(data) });
      }),
    );
  });

  private readonly destroyForm = this.effect(trigger$ =>
    trigger$.pipe(tap(() => this.patchState({ formGroup: undefined }))),
  );
}
